import { el, clear, qs } from "../../utils/dom";
import { getState, setState } from "../../state/store";
import type { ConstraintSet } from "../../types/models";

/**
 * Show a confirm dialog and return true/false
 */
function showConfirmDialog(message: string, confirmText = "Remove"): Promise<boolean> {
    return new Promise((resolve) => {
        const backdrop = el("div", {
            style: "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center;"
        });

        const dialog = el("div", {
            style: "background: var(--card); border-radius: 16px; padding: 24px; max-width: 400px; width: 90%; border: 1px solid var(--border);"
        });

        const label = el("p", { style: "margin: 0 0 16px 0; font-weight: 500;" }, message);

        const buttonRow = el("div", { style: "display: flex; gap: 8px; justify-content: flex-end;" });

        const cancelBtn = el("button", { style: "padding: 8px 16px;" }, "Cancel");
        const confirmBtn = el("button", { class: "danger", style: "padding: 8px 16px;" }, confirmText);

        const close = (result: boolean) => {
            backdrop.remove();
            resolve(result);
        };

        cancelBtn.addEventListener("click", () => close(false));
        confirmBtn.addEventListener("click", () => close(true));

        backdrop.addEventListener("keydown", (e) => {
            if (e.key === "Escape") close(false);
            if (e.key === "Enter") close(true);
        });

        backdrop.addEventListener("click", (e) => {
            if (e.target === backdrop) close(false);
        });

        buttonRow.append(cancelBtn, confirmBtn);
        dialog.append(label, buttonRow);
        backdrop.appendChild(dialog);
        document.body.appendChild(backdrop);

        confirmBtn.focus();
    });
}

export function mountConstraints() {
    const host = qs("#constraints");
    render();

    function render() {
        clear(host);
        const s = getState();
        const cls = s.classes.find(c => c.id === s.activeClassId);

        if (!cls) {
            host.append(el("div", {}, "No class."));
            return;
        }

        const container = el("div", { class: "constraints-editor" });

        // Privacy mode toggle row
        const privacyRow = el("div", { class: "privacy-toggle-row" });
        const privacyLabel = el("label", { class: "toggle-label" },
            el("span", {}, "Privacy Mode"),
            el("span", { class: "toggle-hint" }, "Blur names for screen sharing")
        );
        const toggleSwitch = el("label", { class: "toggle-switch" });
        const blurCheckbox = el("input", { type: "checkbox" }) as HTMLInputElement;
        blurCheckbox.checked = cls.settings.constraintsBlurred ?? false;
        const slider = el("span", { class: "toggle-slider" });
        toggleSwitch.append(blurCheckbox, slider);
        privacyRow.append(privacyLabel, toggleSwitch);
        container.appendChild(privacyRow);

        const isBlurred = cls.settings.constraintsBlurred ?? false;

        // Must be together section
        const togetherSection = renderSection(
            "Must Be Together",
            "Students who should be in the same group",
            cls.constraints.mustTogether,
            true,
            isBlurred
        );

        // Must be apart section
        const apartSection = renderSection(
            "Must Be Apart",
            "Students who should be in different groups",
            cls.constraints.mustApart,
            false,
            isBlurred
        );

        container.append(togetherSection, apartSection);
        host.appendChild(container);

        // Handle blur toggle
        blurCheckbox.addEventListener("change", () => {
            const newBlurred = blurCheckbox.checked;
            setState(ns => {
                const c = current(ns);
                c.settings.constraintsBlurred = newBlurred;
            });
            // Update sections immediately
            [togetherSection, apartSection].forEach(section => {
                const list = section.querySelector('.constraint-list');
                if (list) {
                    if (newBlurred) {
                        list.classList.add('constraints-blurred');
                    } else {
                        list.classList.remove('constraints-blurred');
                    }
                }
            });
        });
    }

    function renderSection(
        title: string,
        subtitle: string,
        constraints: ConstraintSet[],
        together: boolean,
        isBlurred: boolean
    ): HTMLElement {
        const s = getState();
        const cls = s.classes.find(c => c.id === s.activeClassId)!;

        const section = el("div", { class: "constraint-section" });

        // Header
        const header = el("div", { class: "constraint-section-header" });

        const titleEl = el("div", {},
            el("h4", { style: "margin: 0;" }, title),
            el("p", { style: "margin: 0; font-size: 0.75rem; color: var(--muted);" }, subtitle)
        );

        const addBtn = el("button", {}, "+ Add");
        addBtn.addEventListener("click", () => {
            setState(ns => {
                const c = current(ns);
                const arr = together ? c.constraints.mustTogether : c.constraints.mustApart;
                arr.push({ ids: [] });
            });
        });

        header.append(titleEl, addBtn);
        section.appendChild(header);

        // List
        const list = el("div", { class: "constraint-list" });
        if (isBlurred) {
            list.classList.add('constraints-blurred');
        }

        if (constraints.length === 0) {
            list.appendChild(
                el("p", { class: "constraint-empty" }, "No constraints")
            );
        } else {
            constraints.forEach((set, idx) => {
                list.appendChild(renderConstraintItem(set, together, idx, cls.students));
            });
        }

        section.appendChild(list);
        return section;
    }

    function renderConstraintItem(
        set: ConstraintSet,
        together: boolean,
        setIndex: number,
        students: { id: string; name: string }[]
    ): HTMLElement {
        const item = el("div", { class: "constraint-item" });

        const info = el("div", { class: "constraint-item-info" });

        // Icon
        const icon = el("span", {
            class: `constraint-icon ${together ? 'together' : 'apart'}`
        }, together ? '↔' : '↕');

        // Names or selector
        if (set.ids.length === 0) {
            // Show selector for empty constraint
            const selector = el("div", { class: "constraint-selector" });

            const studentSelect = el("select") as HTMLSelectElement;
            studentSelect.appendChild(el("option", { value: "", disabled: true, selected: true }, "Select students..."));
            students.forEach(st => {
                studentSelect.appendChild(el("option", { value: st.id }, st.name));
            });

            studentSelect.addEventListener("change", () => {
                if (!studentSelect.value) return;
                setState(ns => {
                    const c = current(ns);
                    const arr = together ? c.constraints.mustTogether : c.constraints.mustApart;
                    if (arr[setIndex] && !arr[setIndex]!.ids.includes(studentSelect.value)) {
                        arr[setIndex]!.ids.push(studentSelect.value);
                    }
                });
            });

            selector.appendChild(studentSelect);
            info.append(icon, selector);
        } else {
            // Show names as chips with add button
            const namesContainer = el("div", { class: "constraint-names" });

            set.ids.forEach(id => {
                const student = students.find(st => st.id === id);
                if (!student) return;

                const chip = el("span", { class: "constraint-chip" }, student.name);
                const removeBtn = el("span", {
                    class: "constraint-chip-remove",
                    onclick: (e: Event) => {
                        e.stopPropagation();
                        setState(ns => {
                            const c = current(ns);
                            const arr = together ? c.constraints.mustTogether : c.constraints.mustApart;
                            if (arr[setIndex]) {
                                arr[setIndex].ids = arr[setIndex]!.ids.filter((x: string) => x !== id);
                            }
                        });
                    }
                }, "×");
                chip.appendChild(removeBtn);
                namesContainer.appendChild(chip);
            });

            // Add more button
            const addMoreBtn = el("button", { class: "constraint-add-more" }, "+");
            addMoreBtn.addEventListener("click", (e) => {
                e.stopPropagation();
                // Show a dropdown to add more students
                const dropdown = el("select", { class: "constraint-add-select" }) as HTMLSelectElement;
                dropdown.appendChild(el("option", { value: "", disabled: true, selected: true }, "Add..."));
                students.forEach(st => {
                    if (!set.ids.includes(st.id)) {
                        dropdown.appendChild(el("option", { value: st.id }, st.name));
                    }
                });

                dropdown.addEventListener("change", () => {
                    if (!dropdown.value) return;
                    setState(ns => {
                        const c = current(ns);
                        const arr = together ? c.constraints.mustTogether : c.constraints.mustApart;
                        if (arr[setIndex] && !arr[setIndex]!.ids.includes(dropdown.value)) {
                            arr[setIndex]!.ids.push(dropdown.value);
                        }
                    });
                });

                dropdown.addEventListener("blur", () => {
                    dropdown.remove();
                });

                addMoreBtn.replaceWith(dropdown);
                dropdown.focus();
            });
            namesContainer.appendChild(addMoreBtn);

            info.append(icon, namesContainer);
        }

        // Delete button
        const deleteBtn = el("button", { class: "constraint-delete-btn" }, "×");
        let isDeleting = false;
        deleteBtn.addEventListener("click", async (e) => {
            e.stopPropagation();
            if (isDeleting) return;
            isDeleting = true;

            const confirmed = await showConfirmDialog("Remove this constraint?");
            if (!confirmed) {
                isDeleting = false;
                return;
            }

            setState(ns => {
                const c = current(ns);
                const arr = together ? c.constraints.mustTogether : c.constraints.mustApart;
                if (arr[setIndex]) {
                    arr.splice(setIndex, 1);
                }
            });
        });

        item.append(info, deleteBtn);
        return item;
    }

    function current(ns: any) {
        return ns.classes.find((c: any) => c.id === ns.activeClassId)!;
    }

    document.addEventListener("state:update", render);
}
